import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { GetWorkInstructionsSuccessResponse, WorkInstruction as WorkInstructionEntity } from "../../../../../../../types/api/work-instruction/get-work-instructions-success-response";
import { AxiosResponse } from "axios";
import { WorkInstruction } from "../../../../../../../components/work-instruction/work-instruction";
import './company-admin-work-instructions.css';
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { Project } from "../../../../../../../types/api/project/get-project-success-response";
import { QRCode } from "antd";
import * as QRCodeLibrary from 'qrcode';
import { jsPDF } from 'jspdf'
import { useToggle } from "../../../../../../../../../hooks/use-toggle";
import { RootState } from "../../../../../../../../../redux/store";
import { networkCall } from "../../../../../../../../../utils/network-call/network-call";
import { showDefaultErrorMessages } from "../../../../../../../../../utils/show-default-error-message/show-default-error-messages";
import { AlertMessageState, clearAlertMessage, showAlertMessage } from "../../../../../../../../../redux/reducer/alert-message.reducer";
import { DefaultErrorResponse } from "../../../../../../../../../types/api/error/default-error-response/default-error-respose.type";
import { setNeedWideningForWorkInstructions, setSelectedMenuItemKeys } from "../../../../../../../../../redux/reducer/antd-layout-info.reducer";
import { showLoadingAlertMessage } from "../../../../../../../../../utils/show-loading-alert-message/show-loading-alert-message";
import { AlertMessages } from "../../../../../../../../../enums/alert-messages.enum";
import { CreateWorkInstructionSuccessResponse } from "../../../../../../../types/api/work-instruction/create-work-instructions-success-response";
import { ValidationErrorResponseData } from "../../../../../../../../../types/api/error/validation-error-response-data/validation-error-response-data.type.";
import { SortableItem } from "../../../../../../../../../components/sortable-item/sortable-item";
import { getImageUrl } from "../../../../../../../../../utils/image-helper/get-image-url";
import { CMSButton } from "../../../../../../../../../components/cms/button/cms-button.";

type CompanyAdminWorkInstructionsProps = {
    project: Project;
};

export const CompanyAdminWorkInstructions = ({ project }: CompanyAdminWorkInstructionsProps) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [workInstructions, setWorkInstructions] = useState<WorkInstructionEntity[]>([]);
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                delay: 130,
                tolerance: 2
            }
        })
    );
    const [numberOfAllAWorkinstrutionsRequest, setNumberOfAllAWorkinstrutionsRequest] = useState<number>(1);
    const [isQRCodeModalOpen, toggleIsQRCodeModalOpen] = useToggle(false);
    const [qrCodeDataUrl, setQRCodeDataUrl] = useState<string>('#');
    const needWideningForWorkInstructions = useSelector((state: RootState) => state.antdLayoutInfo.needWideningForWorkInstructions);
    const runningWorkInstructionAnimationIDs = useRef(new Set<number>());

    useEffect(() => {
        if (project.QRCode) {
            QRCodeLibrary.toDataURL(project.QRCode)
                .then(dataURL => {
                    setQRCodeDataUrl(dataURL);
                });
        }
    }, [project.QRCode]);

    useEffect(() => {
        networkCall(`/projects/${ project.id }/work-instructions`)
            .then(res => {
                const messageShowed = showDefaultErrorMessages(res);
                if (!res || messageShowed) return;

                const alertMessageState: AlertMessageState = {
                    alertMessageType: 'error',
                    alertMessageText: 'Ismeretlen hiba történt a munkautasítások lekérése során. Kérjük próbálja újra!'
                };
        
                if (res.status === 200) {
                    const getWorkInstructionsRes: AxiosResponse<GetWorkInstructionsSuccessResponse> = res;
                    const _workInstructions = JSON.parse(JSON.stringify(getWorkInstructionsRes.data.workInstructions)) as WorkInstructionEntity[];
                    setWorkInstructions(_workInstructions.sort((a, b) => a.order - b.order));
                    return;
                } else if (res.status === 404) {
        
                    const _res: AxiosResponse<DefaultErrorResponse> = res;
        
                    switch (_res.data.code) {
                        case 4000:
                            alertMessageState.alertMessageText = 'A projekt nem található.';
                            navigate('/cms/company-admin/projects');
                            break;
                    }
        
                }
        
                dispatch(clearAlertMessage());
                dispatch(showAlertMessage(alertMessageState));
            })
            .catch(err => {
                dispatch(clearAlertMessage());
                dispatch(showAlertMessage({
                    alertMessageText: 'Hiba történt.',
                    alertMessageType: 'error'
                }));
            });
    }, [project.id, dispatch, navigate, numberOfAllAWorkinstrutionsRequest]);

    useEffect(() => {
        dispatch(setSelectedMenuItemKeys(['company-admin-projects']));
    }, [dispatch]);

    useEffect(() => {
        if (selectedIndex === null) dispatch(setNeedWideningForWorkInstructions(false));
        else {
            const handleResize = () => {
                if (window.innerWidth < 1650) dispatch(setNeedWideningForWorkInstructions(true));
                else dispatch(setNeedWideningForWorkInstructions(false));
            };
            
            handleResize();
            window.addEventListener('resize', handleResize);

            return () => {
                window.removeEventListener('resize', handleResize);
            };
        }
    }, [selectedIndex, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(setNeedWideningForWorkInstructions(false));
        };
    }, [dispatch])

    const handleWorkInstructionClick = (index: number) => {
        if (runningWorkInstructionAnimationIDs.current.size > 0) {
            return;
        }

        setSelectedIndex(index);
    };

    const handleAddButtonClick = async () => {
        showLoadingAlertMessage();

        const response = await networkCall(`/projects/${ project.id }/work-instructions`, {
            method: 'POST'
        });

        const messageShowed = showDefaultErrorMessages(response);
        if (messageShowed) return;

        dispatch(clearAlertMessage());
        const alertMessageState: AlertMessageState = {
            alertMessageType: 'error',
            alertMessageText: AlertMessages.UnknownErrorTryAgain
        };

        if (response?.status === 201) {
            const res: AxiosResponse<CreateWorkInstructionSuccessResponse> = response;
            setNumberOfAllAWorkinstrutionsRequest(numberOfAllAWorkinstrutionsRequest + 1);
            dispatch(showAlertMessage({
                alertMessageText: 'Új lépés sikeresen hozzáadva.'
            }));
            return;
        } else if (response?.status === 400) {
            const res: AxiosResponse<DefaultErrorResponse> = response;

            switch (res.data.code) {
                case 1000:
                    alertMessageState.alertMessageText = 'HibáSan megadott beviteli mezők.';
                    break;
                case 6002:
                    alertMessageState.alertMessageText = 'A projekthez nem adhat hozzá munkautasítás, mert nincs hozzárendelve a munkautasítás modul.';
                    break;
            }
        } else if (response?.status === 404) {
            const res: AxiosResponse<DefaultErrorResponse> = response;

            switch (res.data.code) {

                case 4000:
                    alertMessageState.alertMessageText = 'A projekt nem található, amihez a munkautasíás hozzá kívánja adni.';
                    break;

            }
        }

        dispatch(clearAlertMessage());
        dispatch(showAlertMessage(alertMessageState));
    };

    const handleDelete = () => {
        setNumberOfAllAWorkinstrutionsRequest(numberOfAllAWorkinstrutionsRequest + 1);
        setSelectedIndex(null);
    };

    const handleEdit = () => {
        setNumberOfAllAWorkinstrutionsRequest(numberOfAllAWorkinstrutionsRequest + 1);
    };

    const handleNewIOTDeviceAdd = () => {
        setNumberOfAllAWorkinstrutionsRequest(numberOfAllAWorkinstrutionsRequest + 1);
    };

    const handleIOTDeviceEdit = () => {
        setNumberOfAllAWorkinstrutionsRequest(numberOfAllAWorkinstrutionsRequest + 1);
    };

    const handleIOTDeviceDelete = () => {
        setNumberOfAllAWorkinstrutionsRequest(numberOfAllAWorkinstrutionsRequest + 1);
    };

    const handleDragEnd = async (event: any) => {
        const { active, over } = event;

        if (!active || !over) return;

        if (active.id !== over.id) {
            showLoadingAlertMessage();

            const oldIndex = workInstructions.findIndex(workInstruction => active.id === workInstruction.id);
            const newIndex = workInstructions.findIndex(workInstruction => over.id === workInstruction.id);
            
            const _workinstructions = arrayMove(workInstructions, oldIndex, newIndex);

            const response = await networkCall('/work-instructions/set-order', {
                method: 'POST',
                body: {
                    ids: _workinstructions.map(workInstruction => workInstruction.id)
                }
            });

            const messageShowed = showDefaultErrorMessages(response);
            if(messageShowed) return;

            dispatch(clearAlertMessage());
            const alertMessageState: AlertMessageState = {
                alertMessageType: 'error',
                alertMessageText: AlertMessages.UnknownErrorTryAgain
            };

            if (response?.status === 200) {
                dispatch(showAlertMessage({
                    alertMessageText: 'Sorbarendezés mentése sikeres.'
                }));
                return;
            } else if (response?.status === 400) {
                const res: AxiosResponse<DefaultErrorResponse<ValidationErrorResponseData>> = response;
    
                switch (res.data.code) {
                    case 1000:
                        alertMessageState.alertMessageText = 'HibáSan megadott beviteli mezők.';
                        break;
                }
            } else if (response?.status === 404) {
                const res: AxiosResponse<DefaultErrorResponse> = response;
    
                switch (res.data.code) {
    
                    case 6000:
                        alertMessageState.alertMessageText = 'A sorbarendezendő lépések valamelyike nem létezik.';
                        break;
                    case 6001:
                        alertMessageState.alertMessageText = 'A sorbarendezendő lépések nem egy csoportba tartoznak.';
                        break;
    
                }
            }
    
            dispatch(clearAlertMessage());
            dispatch(showAlertMessage(alertMessageState));
        }
    };

    const handleDownloadClick = () => {
        if (qrCodeDataUrl) {
            const pdf = new jsPDF();
            pdf.text(`A "${ project.name }" QR kódja`, 105, 20, {
                align: 'center',
                maxWidth: 140
            });
            pdf.addImage(qrCodeDataUrl.split(',')[1] as any, 'PNG', 20, 50, 170, 170);
            pdf.save(`QR_${ new Date().getTime().toString() }.pdf`);
        }
    };

    const handlePrintClick = () => {
        if (qrCodeDataUrl) {
            const pdf = new jsPDF();
            pdf.text(`A "${ project.name }" QR kódja`, 105, 20, {
                align: 'center',
                maxWidth: 140
            });
            pdf.addImage(qrCodeDataUrl.split(',')[1] as any, 'PNG', 20, 50, 170, 170);
            window.open(URL.createObjectURL(pdf.output('blob')), "_blank");
        }
    };

    const handleSavedWorkInstruction = (editedWorkInstruction: WorkInstructionEntity) => {
        const nextWorkInstructions = workInstructions.map(workInstruction => {
            if (workInstruction.id === editedWorkInstruction.id) return editedWorkInstruction;
            else return workInstruction;
        });

        setWorkInstructions(nextWorkInstructions);
    };

    return (
        <div
            className='company-admin-work-instructions'
            style={
                needWideningForWorkInstructions ? {
                    marginLeft: '385px'
                } : {
                    justifyContent: 'center'
                }
            }
        >
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <div className='work-instructions'>
                    <SortableContext
                        items={needWideningForWorkInstructions ? [] : workInstructions}
                        strategy={verticalListSortingStrategy}
                    >
                        {workInstructions.map((workInstruction, index) => {
                            return (
                                <React.Fragment key={workInstruction.id}>
                                    <SortableItem id={workInstruction.id}>
                                        <WorkInstruction
                                            index={index}
                                            selectedWorkInstructionIndex={selectedIndex}
                                            workInstruction={workInstruction}
                                            onWorkInstructionClick={handleWorkInstructionClick}
                                            onDelete={handleDelete}
                                            onEdit={handleEdit}
                                            onClose={() => setSelectedIndex(null)}
                                            onNewIOTDeviceAdd={handleNewIOTDeviceAdd}
                                            onIOTDeviceEdit={handleIOTDeviceEdit}
                                            onIOTDeviceDelete={handleIOTDeviceDelete}
                                            onSavedWorkInstruction={handleSavedWorkInstruction}
                                            onAnimationStart={() => runningWorkInstructionAnimationIDs.current.add(workInstruction.id)}
                                            onAnimationEnd={() => runningWorkInstructionAnimationIDs.current.delete(workInstruction.id)}
                                        />
                                    </SortableItem>
                                    <img className={`arrow-image ${ selectedIndex !== null ? 'obscure' : '' }`} src={getImageUrl('arrow.png')} alt="arrow" />
                                </React.Fragment>
                            );
                        })}
                        <img
                            className={`add-button mb-4 ${workInstructions.length > 0 ? 'mt-5' : ''}`}
                            src={getImageUrl('add-button.png')}
                            alt="add-button"
                            onClick={handleAddButtonClick}
                        />
                    </SortableContext>
                </div>
            </DndContext>

            <div className="qr-and-export-btn-container d-flex gap-3">
                <CMSButton text="QR" type="blue" iconSrc={getImageUrl('qr-code-icon.png')} onClick={toggleIsQRCodeModalOpen} />
                <CMSButton text="Export" type="pink" iconSrc={getImageUrl('export-icon.png')} />
            </div>

            <Modal isOpen={isQRCodeModalOpen} toggle={toggleIsQRCodeModalOpen}>
                <div className="text-end">
                    <img
                        style={{ width: '50px', padding: '10px 10px 0 0' }}
                        src={getImageUrl('close-button.png')}
                        alt="close-button"
                        title="Bezárás"
                        className="cursor-pointer"
                        onClick={toggleIsQRCodeModalOpen}
                    />
                </div>
                <ModalHeader className="justify-content-center blue-font pt-0">
                    A "{project.name}" QR kódja
                </ModalHeader>
                <ModalBody className='d-flex justify-content-center'>
                    <QRCode value={project.QRCode} size={220} style={{ border: '18px solid #DA4EA4', borderRadius: '20px', padding: '0' }} />
                </ModalBody>
                <ModalFooter className='justify-content-center gap-3 mb-3'>
                    <CMSButton text="Nyomtatás" type='pink' iconSrc={getImageUrl('printer-icon.png')} onClick={handlePrintClick} />
                    <CMSButton text="Letöltés" type='white' iconSrc={getImageUrl('download-icon.png')} onClick={handleDownloadClick} />
                </ModalFooter>
            </Modal>
        </div>
    );
};